import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// Protected Apps deep-dive page
const { useState, useEffect, useRef } = React;

const Icon = {
  arrow: (p) => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}><path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/></svg>,
  copy: (p) => <svg width="11" height="11" viewBox="0 0 12 12" fill="none" {...p}><rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1"/><path d="M2 8V2h6" stroke="currentColor" strokeWidth="1"/></svg>,
  lock: (p) => <svg width="9" height="9" viewBox="0 0 10 10" fill="none" {...p}><rect x="2" y="5" width="6" height="4" stroke="currentColor" strokeWidth="1"/><path d="M3 5V3.5C3 2.4 3.9 1.5 5 1.5C6.1 1.5 7 2.4 7 3.5V5" stroke="currentColor" strokeWidth="1"/></svg>,
};

// ========= Hero =========
function Hero() {
  return (
    <section className="section section-dark pa-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="reveal">
          <span className="pa-eyebrow">
            <span className="eb-dot"></span>
            PROTECTED APPS · <strong>The flagship feature</strong>
          </span>
          <h1>
            Any local app,<br/>
            <em>private to the people</em><br/>
            you choose.
          </h1>
          <p className="pa-hero-sub">
            Make grafana, jupyter, your home dashboard, your dev server, or that one weird internal tool reachable as a real https URL — without static IPs, port forwarding, DNS configuration, or open inbound ports.
          </p>
          <div className="pa-hero-ctas">
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Protect my first app — free <Icon.arrow /></span></a>
            <a href="#how-it-works" className="btn btn-on-dark btn-lg"><span>See how it works</span></a>
          </div>
          <div className="pa-hero-trust">
            <span><span className="check">✓</span>Free for personal use</span>
            <span><span className="check">✓</span>Setup in 60 seconds</span>
            <span><span className="check">✓</span>No port forwarding</span>
            <span><span className="check">✓</span>Real TLS, real hostname</span>
            <span><span className="check">✓</span>Revoke instantly</span>
          </div>
        </div>

        {/* Big stage visualization */}
        <div className="pa-stage reveal" style={{ animationDelay: '160ms' }}>
          <span className="pa-stage-label">LIVE TUNNEL · grafana-prod.acme.named.network</span>
          <span className="pa-stage-corner tl"></span>
          <span className="pa-stage-corner tr"></span>
          <span className="pa-stage-corner bl"></span>
          <span className="pa-stage-corner br"></span>
          <BigStageViz />
        </div>

        {/* Stats row */}
        <div className="pa-stats">
          {[
            { num: '60s', label: 'Setup time', desc: 'From install command to first private link' },
            { num: '0', label: 'Open ports', desc: 'Outbound only. No inbound exposure ever.' },
            { num: '~12ms', label: 'Median latency', desc: 'Direct peer-to-peer where possible' },
            { num: '99.97%', label: 'Edge uptime', desc: 'Last 90 days. Public status page.' },
          ].map((s, i) => (
            <div key={i} className="pa-stat reveal" style={{ animationDelay: `${i*80}ms` }}>
              <div className="num"><em>{s.num.match(/[\d.]+/)?.[0] || s.num}</em>{s.num.replace(/[\d.]+/, '')}</div>
              <div className="lbl">{s.label}</div>
              <div className="desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BigStageViz() {
  return (
    <svg viewBox="0 0 1200 460" style={{ width: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 1 }}>
      {/* Background mesh dots */}
      {Array.from({length: 9}).map((_, r) =>
        Array.from({length: 18}).map((_, c) =>
          <circle key={`${r}-${c}`} cx={40 + c * 65} cy={40 + r * 50} r="0.7" fill="rgba(255,255,255,0.08)" />
        )
      )}

      {/* Friend (left) */}
      <g transform="translate(120, 230)">
        <text x="0" y="-100" fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="2">YOUR FRIEND'S BROWSER</text>
        <rect x="-90" y="-72" width="180" height="115" rx="3" fill="var(--bg-dark-card)" stroke="var(--border-dark)" strokeWidth="1"/>
        <rect x="-90" y="-72" width="180" height="22" fill="var(--bg-dark-elev)" stroke="var(--border-dark)" strokeWidth="1"/>
        <circle cx="-78" cy="-61" r="3" fill="#3a3a3a"/>
        <circle cx="-68" cy="-61" r="3" fill="#3a3a3a"/>
        <circle cx="-58" cy="-61" r="3" fill="#3a3a3a"/>
        <rect x="-78" y="-44" width="156" height="14" fill="var(--bg-dark)" stroke="var(--border-dark)" strokeWidth="0.5" rx="1"/>
        <text x="-72" y="-34" fontFamily="var(--font-mono)" fontSize="8" fill="var(--accent)">🔒 grafana-prod.acme.named.network</text>
        <rect x="-78" y="-22" width="156" height="6" fill="var(--accent)" opacity="0.6"/>
        <rect x="-78" y="-12" width="100" height="3" fill="var(--text-dark-faint)"/>
        <rect x="-78" y="-5" width="120" height="3" fill="var(--text-dark-faint)"/>
        <rect x="-78" y="2" width="80" height="3" fill="var(--text-dark-faint)"/>
        <rect x="-78" y="14" width="156" height="22" fill="var(--bg-dark)" stroke="var(--accent)" strokeWidth="0.5" rx="1"/>
        <text x="0" y="29" fontFamily="var(--font-mono)" fontSize="8" fill="var(--accent)" textAnchor="middle">live · 200 OK</text>
        <text x="0" y="80" fontFamily="var(--font-mono)" fontSize="9" fill="var(--text-dark-faint)" textAnchor="middle" letterSpacing="1">priya@team.io</text>
        <text x="0" y="95" fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-faint)" textAnchor="middle" letterSpacing="0.5">invited · 4d ago</text>
      </g>

      {/* Tunnel cloud (center) */}
      <g transform="translate(600, 230)">
        <rect x="-180" y="-130" width="360" height="240" fill="none" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 6" opacity="0.5"/>
        <text x="-170" y="-110" fontFamily="var(--font-mono)" fontSize="10" fill="var(--accent)" letterSpacing="2">NAMED.NETWORK MESH · GLOBAL EDGE</text>

        {/* Edge nodes around center */}
        {[
          { x: -110, y: -50, label: 'iad' },
          { x: 110, y: -50, label: 'fra' },
          { x: -130, y: 60, label: 'sfo' },
          { x: 130, y: 60, label: 'sin' },
        ].map((n, i) => (
          <g key={i}>
            <circle cx={n.x} cy={n.y} r="14" fill="var(--bg-dark-card)" stroke="var(--border-dark-strong)" strokeWidth="1"/>
            <text x={n.x} y={n.y + 3} fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-muted)" textAnchor="middle">{n.label}</text>
          </g>
        ))}

        {/* Center secure capsule */}
        <circle cx="0" cy="0" r="44" fill="var(--bg-dark-card)" stroke="var(--accent)" strokeWidth="1.5"/>
        <circle cx="0" cy="0" r="44" fill="var(--accent-fade)"/>
        <circle cx="0" cy="0" r="36" fill="none" stroke="var(--accent)" strokeWidth="0.5" strokeDasharray="2 3"/>
        <text x="0" y="-3" fontFamily="var(--font-mono)" fontSize="9" fill="var(--accent)" textAnchor="middle" letterSpacing="2">TLS 1.3</text>
        <text x="0" y="11" fontFamily="var(--font-mono)" fontSize="8" fill="var(--accent)" textAnchor="middle" letterSpacing="1">end-to-end</text>

        <text x="0" y="130" fontFamily="var(--font-mono)" fontSize="9" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="2">AUTH · IDENTITY · ROUTING · NEVER STORES YOUR TRAFFIC</text>
      </g>

      {/* Your computer (right) */}
      <g transform="translate(1080, 230)">
        <text x="0" y="-100" fontFamily="var(--font-mono)" fontSize="10" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="2">YOUR COMPUTER</text>
        <rect x="-90" y="-72" width="180" height="115" rx="3" fill="var(--bg-dark-card)" stroke="var(--border-dark)" strokeWidth="1"/>
        <rect x="-90" y="-72" width="180" height="22" fill="var(--bg-dark-elev)"/>
        <text x="-78" y="-58" fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-muted)" letterSpacing="1">~ namedn agent · ready</text>
        <text x="78" y="-58" fontFamily="var(--font-mono)" fontSize="9" fill="var(--success)" textAnchor="end">●</text>

        {[
          { y: -38, name: 'grafana', port: '3000', protected: true },
          { y: -20, name: 'jupyter', port: '8888', protected: false },
          { y: -2, name: 'postgres', port: '5432', protected: true },
        ].map((a, i) => (
          <g key={i}>
            <rect x="-78" y={a.y - 6} width="156" height="14" fill={a.protected ? 'var(--accent-fade)' : 'transparent'} stroke={a.protected ? 'var(--accent)' : 'var(--border-dark-subtle)'} strokeWidth="0.5"/>
            <circle cx="-70" cy={a.y + 1} r="2" fill={a.protected ? 'var(--accent)' : 'var(--text-dark-faint)'}/>
            <text x="-62" y={a.y + 4} fontFamily="var(--font-mono)" fontSize="8" fill={a.protected ? 'var(--accent)' : 'var(--text-dark-secondary)'}>{a.name}:{a.port}</text>
            <text x="74" y={a.y + 4} fontFamily="var(--font-mono)" fontSize="7" fill={a.protected ? 'var(--accent)' : 'var(--text-dark-faint)'} textAnchor="end">{a.protected ? 'PROTECTED' : 'local'}</text>
          </g>
        ))}

        <text x="0" y="32" fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-faint)" textAnchor="middle" letterSpacing="1">— firewall: outbound only —</text>

        <text x="0" y="80" fontFamily="var(--font-mono)" fontSize="9" fill="var(--text-dark-faint)" textAnchor="middle" letterSpacing="1">mac-studio-01 · macOS 14.4</text>
        <text x="0" y="95" fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-faint)" textAnchor="middle" letterSpacing="0.5">last beat 0.3s ago</text>
      </g>

      {/* Animated tunnel lines */}
      <path d="M210 230 Q 405 230 555 230" className="route-line live" />
      <path d="M645 230 Q 800 230 990 230" className="route-line live" />

      {/* Annotation: no port forward */}
      <g transform="translate(1080, 380)">
        <rect x="-100" y="-12" width="200" height="22" fill="none" stroke="var(--border-dark-strong)" strokeWidth="0.5" strokeDasharray="2 3"/>
        <text x="0" y="3" fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="1.5">NO INBOUND PORTS · OUTBOUND CONNECTION ONLY</text>
      </g>
      <g transform="translate(120, 380)">
        <rect x="-100" y="-12" width="200" height="22" fill="none" stroke="var(--border-dark-strong)" strokeWidth="0.5" strokeDasharray="2 3"/>
        <text x="0" y="3" fontFamily="var(--font-mono)" fontSize="8" fill="var(--text-dark-muted)" textAnchor="middle" letterSpacing="1.5">NORMAL HTTPS · NO CLIENT INSTALL</text>
      </g>
    </svg>
  );
}

// ========= Workflow (4 steps) =========
function Workflow() {
  return (
    <section className="section section-dark" id="how-it-works" style={{ paddingTop: 96, paddingBottom: 96 }}>
      <div className="container">
        <div className="section-h">
          <span className="section-label">The flow</span>
          <h2 style={{ color: 'var(--text-dark-primary)' }}>From local to live in four steps.</h2>
          <p>No router config. No DNS records. No certificates to renew. The agent does the introspection, our edge does the routing, you do the clicks.</p>
        </div>

        <div className="workflow">
          {[
            { num: '01', dur: '~30s', title: 'Install the agent', body: 'One curl command on the computer where your app already runs. Verifies, installs, joins your workspace.' },
            { num: '02', dur: '~5s', title: 'Pick from detected apps', body: 'The agent reports what it finds: grafana, jupyter, postgres — by port and process name. Click one.' },
            { num: '03', dur: '~10s', title: 'Confirm the private hostname', body: 'We suggest a safe link like grafana.acme.named.network. Edit if you want, accept to mint a real TLS cert instantly.' },
            { num: '04', dur: '~15s', title: 'Invite who can open it', body: 'Add emails. They sign in once and the link works. Revoke any time. Approve pending requests inline.' },
          ].map((s, i) => (
            <div key={i} className="wf-step">
              <div className="wf-num"><span>STEP {s.num}</span><span className="dur">{s.dur}</span></div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
              <div className="wf-arrow"><Icon.arrow /></div>
            </div>
          ))}
        </div>

        {/* Inline CTA */}
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Start in 60 seconds <Icon.arrow /></span></a>
          <div style={{ marginTop: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            No credit card · 1 protected app free forever
          </div>
        </div>
      </div>
    </section>
  );
}

// ========= Feature deep-dives =========
function Features() {
  return (
    <section className="section section-light">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">What you actually get</span>
          <h2>Everything a hosted SaaS gives you, for code that runs at home.</h2>
          <p>Real https URLs. Real TLS. Real auth. Real audit. The difference is the app stays on your computer and never goes through someone else's server.</p>
        </div>

        <FeatureRow
          flip={false}
          tag="01 · DISCOVERY"
          title="The agent finds your apps automatically."
          body="Stop typing local addresses. The agent watches for processes that bind to local ports and surfaces them in the dashboard with their real names — grafana, jupyter, redis, whatever. You click; we configure."
          bullets={[
            "Auto-detects apps as soon as you start them",
            "Suggests <strong>safe link names</strong> based on the process",
            "Shows hostname conflicts before you commit",
            "Manual fallback for apps not detected automatically",
          ]}
          viz={<DiscoveryViz />}
        />

        <FeatureRow
          flip={true}
          tag="02 · PRIVATE HOSTNAMES"
          title="Real https URLs people can actually bookmark."
          body="Each protected app gets a permanent, private hostname like grafana.acme.named.network. Auto-issued TLS, auto-renewed forever. Looks and behaves exactly like a normal SaaS — except only your invited people can reach it."
          bullets={[
            "Free TLS certificates on every app, auto-renewed",
            "Custom subdomains per workspace",
            "Private named.network hostnames on every protected app",
            "<strong>HTTP/2 and WebSocket</strong> support out of the box",
          ]}
          viz={<HostnameViz />}
        />

        <FeatureRow
          flip={false}
          tag="03 · ACCESS CONTROL"
          title="Email-based invites and access rules."
          body="No tokens to copy-paste. No corporate-IT flow. Type a person's email, send the invite, and manage access from the dashboard. They sign in once and the app is reachable from any browser. Revoke any time and the link stops working immediately."
          bullets={[
            "Per-app sharing — not workspace-wide",
            "Email invites plus direct access rules",
            "<strong>Public access requests</strong> with approval queue",
            "Audit log of who opened what, when",
          ]}
          viz={<AccessViz />}
        />

        <FeatureRow
          flip={true}
          tag="04 · OBSERVABILITY"
          title="See it working. See it failing. Fix it fast."
          body="Every app shows status, last access, recent requests, and hosting-agent health. When something breaks — your laptop sleeps, your ISP flaps, an app crashes — you see exactly which piece, before your friend pings you about it."
          bullets={[
            "Status: ready · awaiting first check · agent offline · off",
            "<strong>Recent request history</strong> per protected app",
            "Continuous hosting-agent status and heartbeat visibility",
            "Disable an app instantly without deleting config",
          ]}
          viz={<ObservabilityViz />}
        />
      </div>
    </section>
  );
}

function FeatureRow({ flip, tag, title, body, bullets, viz }) {
  return (
    <div className={`feature-row ${flip ? 'flip' : ''}`}>
      <div className="fr-copy reveal">
        <span className="section-label on-light">{tag}</span>
        <h3>{title}</h3>
        <p>{body}</p>
        <ul className="fr-bullets">
          {bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: b }} />)}
        </ul>
      </div>
      <div className="reveal" style={{ animationDelay: '120ms' }}>{viz}</div>
    </div>
  );
}

function DiscoveryViz() {
  return (
    <div className="viz-frame" data-label="DETECTED ON mac-studio-01">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { name: 'grafana', port: 3000, proc: 'grafana-server', sug: 'grafana.acme', protected: false },
          { name: 'jupyter', port: 8888, proc: 'python3', sug: 'lab.acme', protected: true },
          { name: 'postgres', port: 5432, proc: 'postgres', sug: 'pg.acme', protected: false },
          { name: 'plex', port: 32400, proc: 'Plex Media Server', sug: 'plex.acme', protected: false },
          { name: 'home-asst', port: 8123, proc: 'hass', sug: 'home.acme', protected: false },
        ].map((a, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '12px 14px', background: a.protected ? 'var(--accent-fade)' : 'var(--bg-light-card)', border: a.protected ? '1px solid var(--accent)' : '1px solid var(--border-light)', borderRadius: 2 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-light-primary)' }}>{a.name}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-muted)' }}>:{a.port} · {a.proc}</span>
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: a.protected ? 'var(--accent)' : 'var(--text-light-muted)', marginTop: 4 }}>
                → {a.sug}.named.network
              </div>
            </div>
            {a.protected ? (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>● protected</span>
            ) : (
              <button style={{ fontFamily: 'var(--font-mono)', fontSize: 9, padding: '6px 10px', background: 'transparent', border: '1px solid var(--text-light-primary)', color: 'var(--text-light-primary)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', borderRadius: 2 }}>protect →</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function HostnameViz() {
  return (
    <div className="viz-frame" data-label="grafana.acme.named.network · TLS handshake">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div className="browser" style={{ marginBottom: 16 }}>
        <div className="browser-bar">
          <div className="browser-dots"><span/><span/><span/></div>
          <div className="browser-url">
            <span className="lock"><Icon.lock /></span>
            grafana.acme.named.network/dashboards
          </div>
        </div>
        <div style={{ height: 100, background: 'var(--bg-light-alt)', padding: 14, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-light-muted)' }}>
          <div style={{ marginBottom: 6 }}>📊 grafana · production overview</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <div style={{ flex: 1, height: 24, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}></div>
            <div style={{ flex: 1, height: 24, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}></div>
          </div>
          <div style={{ height: 40, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
            <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 100 40">
              <path d="M0 30 L15 22 L30 24 L45 14 L60 18 L75 8 L90 12 L100 6" stroke="var(--accent)" strokeWidth="1" fill="none"/>
            </svg>
          </div>
        </div>
      </div>
      <div style={{ background: 'var(--bg-dark)', padding: 12, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-dark-secondary)', borderRadius: 2 }}>
        <div style={{ color: 'var(--success)' }}>✓ TLS 1.3 · Let's Encrypt R3 · valid 89 days</div>
        <div style={{ color: 'var(--text-dark-muted)' }}>auto-renews · zero config</div>
      </div>
    </div>
  );
}

function AccessViz() {
  return (
    <div className="viz-frame" data-label="ACCESS · grafana-prod">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input readOnly defaultValue="alex@friend.dev" style={{ flex: 1, padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 12, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }} />
          <select defaultValue="direct access" style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 12, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}>
            <option>direct access</option>
            <option>temporary access</option>
            <option>workspace member</option>
          </select>
          <button style={{ padding: '0 14px', fontFamily: 'var(--font-mono)', fontSize: 10, background: 'var(--accent)', color: '#fff', border: 'none', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', borderRadius: 2 }}>Invite</button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[
          { e: 'priya@team.io', r: 'direct access', a: 'P', t: '4d ago', s: 'active' },
          { e: 'jin@team.io', r: 'invite sent', a: 'J', t: '2h ago', s: 'pending' },
          { e: 'maya@friend.dev', r: 'temporary access', a: 'M', t: 'just now', s: 'active' },
          { e: 'request: dana@new.com', r: '—', a: 'D', t: '12m ago', s: 'request' },
        ].map((p, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr auto auto', gap: 10, alignItems: 'center', padding: '10px 12px', background: p.s === 'request' ? 'var(--accent-fade)' : 'var(--bg-light-card)', border: p.s === 'request' ? '1px solid var(--accent)' : '1px solid var(--border-light)', borderRadius: 2 }}>
            <span style={{ width: 24, height: 24, background: 'var(--bg-light-alt)', border: '1px solid var(--border-light)', borderRadius: '50%', display: 'grid', placeItems: 'center', fontFamily: 'var(--font-mono)', fontSize: 10 }}>{p.a}</span>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{p.e}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-light-muted)', marginTop: 2 }}>{p.r} · {p.t}</div>
            </div>
            {p.s === 'pending' && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>● pending</span>}
            {p.s === 'request' && (
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={{ fontSize: 8, fontFamily: 'var(--font-mono)', padding: '3px 6px', background: 'var(--accent)', color: '#fff', border: 'none', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer' }}>approve</button>
                <button style={{ fontSize: 8, fontFamily: 'var(--font-mono)', padding: '3px 6px', background: 'transparent', color: 'var(--text-light-muted)', border: '1px solid var(--border-light)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer' }}>deny</button>
              </div>
            )}
            {p.s === 'active' && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-light-muted)' }}>···</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ObservabilityViz() {
  return (
    <div className="viz-frame" data-label="LIVE · grafana-prod">
      <span className="viz-corner tl"></span><span className="viz-corner tr"></span>
      <span className="viz-corner bl"></span><span className="viz-corner br"></span>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div style={{ padding: 14, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-light-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Status</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 18, color: 'var(--success)', marginTop: 6, letterSpacing: '-0.01em', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ position: 'relative', width: 8, height: 8, borderRadius: '50%', background: 'var(--success)' }}>
              <span className="pulse-ring" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--success)' }}></span>
            </span>
            ready
          </div>
        </div>
        <div style={{ padding: 14, background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-light-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Last access</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 18, color: 'var(--text-light-primary)', marginTop: 6, letterSpacing: '-0.01em' }}>3s ago</div>
        </div>
      </div>
      <div style={{ background: 'var(--bg-light-card)', border: '1px solid var(--border-light)', borderRadius: 2, padding: '12px 14px' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-light-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Recent requests</div>
        {[
          { c: 200, m: 'GET', p: '/api/dashboards', t: '0.2s', who: 'priya' },
          { c: 200, m: 'GET', p: '/api/datasources', t: '0.4s', who: 'priya' },
          { c: 200, m: 'POST', p: '/api/annotations', t: '0.6s', who: 'maya' },
          { c: 200, m: 'GET', p: '/api/dashboards/uid/abc', t: '1.1s', who: 'maya' },
        ].map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '34px 36px 1fr auto auto', gap: 8, alignItems: 'center', padding: '4px 0', fontFamily: 'var(--font-mono)', fontSize: 10 }}>
            <span style={{ color: 'var(--success)' }}>{r.c}</span>
            <span style={{ color: 'var(--text-light-muted)' }}>{r.m}</span>
            <span style={{ color: 'var(--text-light-primary)' }}>{r.p}</span>
            <span style={{ color: 'var(--accent)' }}>{r.who}</span>
            <span style={{ color: 'var(--text-light-faint)' }}>{r.t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========= Mid-page CTA =========
function MidCTA() {
  return (
    <section className="section section-light-alt" style={{ padding: '0' }}>
      <div className="container">
        <div className="cta-inline">
          <div className="cta-inline-text">
            <h4>Skeptical? Try it on your homelab in 60 seconds.</h4>
            <p>The free plan never expires. Two devices, one protected app, zero credit card. Run the install, share something with a friend, see it work.</p>
          </div>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create my workspace <Icon.arrow /></span></a>
        </div>
      </div>
    </section>
  );
}

// ========= Comparison =========
function Comparison() {
  const rows = [
    { feat: 'Setup time', port: 'hours', cf: '~30 min', tail: '~10 min', vpn: '60 seconds' },
    { feat: 'Open inbound ports', port: <span className="x">required</span>, cf: <span className="check">none</span>, tail: <span className="check">none</span>, vpn: <span className="check">none</span> },
    { feat: 'Public TLS hostname', port: 'manual', cf: <span className="check">yes</span>, tail: <span className="partial">internal only</span>, vpn: <span className="check">yes</span> },
    { feat: 'No client install for guests', port: <span className="check">yes</span>, cf: <span className="check">yes</span>, tail: <span className="x">required</span>, vpn: <span className="check">yes</span> },
    { feat: 'Per-app sharing', port: <span className="x">no</span>, cf: <span className="partial">access policies</span>, tail: <span className="partial">ACLs</span>, vpn: <span className="check">yes, native</span> },
    { feat: 'Auto-discovers local apps', port: <span className="x">no</span>, cf: <span className="x">no</span>, tail: <span className="x">no</span>, vpn: <span className="check">yes</span> },
    { feat: 'Free for personal use', port: 'pay for IP', cf: <span className="partial">limited</span>, tail: <span className="check">yes</span>, vpn: <span className="check">yes, forever</span> },
    { feat: 'AI/MCP scoped tokens', port: <span className="x">—</span>, cf: <span className="x">—</span>, tail: <span className="x">—</span>, vpn: <span className="check">yes</span> },
  ];
  return (
    <section className="section section-light">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Honest comparison</span>
          <h2>How named.network stacks up.</h2>
          <p>We're not pretending to be the only option. Here's where each approach lands for the specific job of "share my local app with people I trust."</p>
        </div>

        <div className="compare">
          <div className="compare-head">
            <div></div>
            <div>Port forwarding</div>
            <div>Cloudflare Tunnel</div>
            <div>Tailscale</div>
            <div className="col-vpn">● named.network</div>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="compare-row">
              <div className="row-label">{r.feat}</div>
              <div>{r.port}</div>
              <div>{r.cf}</div>
              <div>{r.tail}</div>
              <div className="col-vpn">{r.vpn}</div>
            </div>
          ))}
        </div>

        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-light-muted)', textAlign: 'center', marginTop: 18 }}>
          Compared in good faith. Tailscale and Cloudflare Tunnel are excellent for adjacent jobs — we just optimized hard for this one.
        </p>
      </div>
    </section>
  );
}

// ========= Use cases (compact) =========
function UseCases() {
  const cases = [
    { title: 'Show your boss the staging dashboard.', body: "From the train. From a hotel. Without VPN client install on their laptop. The link just works." },
    { title: 'Give a contractor temporary access.', body: "Invite their email, set a temporary access window, and expire it in a week. No SSH keys, no IP allowlists, no shared credentials." },
    { title: 'Self-host a thing without buying a server.', body: "Your laptop is the server. Sleep it, the app pauses. Wake it, the app resumes. Friends never notice." },
    { title: 'Replace screenshots in your bug reports.', body: "Make your local repro a private link. The maintainer opens it, sees the bug, you both move on." },
    { title: 'Open a port to your AI agent, not the world.', body: "Pair Protected Apps with scoped tokens. Your agent can hit your dev API without anyone else being able to." },
    { title: 'Run a homelab service for the family.', body: "Plex, Home Assistant, Jellyfin, your PhotoPrism. Everyone in the household, nobody outside it." },
  ];
  return (
    <section className="section section-light-alt">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Real things people do with it</span>
          <h2 style={{ maxWidth: 760, margin: '14px auto 0' }}>Six concrete examples that go live the same day you sign up.</h2>
        </div>
        <div className="usecase-grid">
          {cases.map((c, i) => (
            <div key={i} className="usecase-card reveal" style={{ animationDelay: `${i*60}ms` }}>
              <div className="uc-icon" style={{ width: 28, height: 28, fontSize: 11 }}>{String(i+1).padStart(2,'0')}</div>
              <h3 style={{ fontSize: 18 }}>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========= FAQ =========
function FAQ() {
  const faqs = [
    { q: 'Do I need a public IP, static DNS, or to open ports?', a: "No to all three. The agent on your computer makes an outbound connection to our edge. Traffic comes back through that connection. Your firewall stays closed." },
    { q: 'Is my traffic actually private? Do you read it?', a: "No. The TLS session is end-to-end between your friend's browser and your computer. Our edge routes encrypted bytes — we cannot read them. We never store request bodies." },
    { q: 'What happens when my computer goes to sleep?', a: "The app shows as 'agent offline' and the link returns a friendly 502-style page. Wake the computer; it auto-reconnects in seconds; the link works again." },
    { q: 'Can I use my own domain yet?', a: "Not yet. Today each protected app gets a private named.network hostname with managed TLS. Custom domains remain a future paid-plan capability, not a current self-serve feature." },
    { q: 'How does this compare to running ngrok?', a: "ngrok is great for temporary tunnels during development. named.network is built for permanent, named, access-controlled apps shared with multiple people. Different shape." },
    { q: 'Do I have to trust named.network with my app traffic?', a: "The app stays on your computer. Access is routed through the named.network edge, but the traffic itself remains encrypted end-to-end between the browser and your app. We handle routing and identity, not your app data." },
    { q: 'What if I want to stop using it?', a: "Delete your workspace from billing settings; we delete your data within 30 days. The agent uninstalls with one command. Nothing is held hostage." },
    { q: "How do I revoke access if I shared something I shouldn't have?", a: "Open the app's detail page, click revoke next to the person, confirm. Their session is terminated within seconds. They cannot reconnect." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="section section-light" id="faq">
      <div className="container-narrow">
        <div className="section-h">
          <span className="section-label on-light">FAQ</span>
          <h2>The questions we get most.</h2>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{f.q}</span>
                <span className="plus">+</span>
              </button>
              <div className="faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========= Final CTA =========
function FinalCTA() {
  return (
    <section className="section section-dark" style={{ padding: '128px 0' }}>
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="cta-final">
          <span className="section-label">Last thing</span>
          <h2>Your first protected app<br/>is a single command away.</h2>
          <p>Free for personal use, forever. One protected app across two devices. Plenty for a homelab, a side project, or proving the concept before you bring your team.</p>
          <div style={{ marginTop: 36, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            <div className="terminal-card" style={{ textAlign: 'left' }}>
              <div className="terminal-head">
                <span className="terminal-meta">~ install · one step</span>
                <button className="terminal-tab"><Icon.copy /> copy</button>
              </div>
              <div className="terminal-body">
                <div><span className="prompt">$</span><span className="com">curl -fsSL https://named.network/install.sh | sh</span></div>
              </div>
            </div>
          </div>
          <div className="ctas">
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Protect my first app — free <Icon.arrow /></span></a>
            <a href="https://app.named.network/login" className="btn btn-on-dark btn-lg"><span>Sign in</span></a>
          </div>
          <div style={{ marginTop: 24, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-faint)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            No credit card · 60-second setup · Cancel any time
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <>
      <SiteNav />
      <Hero />
      <Workflow />
      <Features />
      <MidCTA />
      <Comparison />
      <UseCases />
      <FAQ />
      <FinalCTA />
      <SiteFooter />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

